/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Button, ButtonIcon, Icon, Typography } from "../..";
import { getGlobal } from "../../../global";
import PropTypes from 'prop-types';
import { forwardRef, Fragment, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animations } from "../../../styles/animation";
import { hexToRGBA } from "../../../styles/color-helper";
import { bgColor, border, borderRadius, borderRadius50, boxBorder, breakWord, cursorPointer, displayBlock, displayFlex, flexCol, flexRow, inset, itemsCenter, justifyCenter, justifyEnd, mg, mgb, mgr, mgt, overflowAuto, overflowHidden, parseHeight, parseMaxWidthHeight, parseMinHeight, parseMinWidth, parseWidth, parseWidthHeight, pd, positionFixed, positionRelative, textColor, textEllipsis, textUnderline, truncate, z } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { classNames, useDelayUnmount } from "../../../utils";
const {
  colors: {
    system: {
      white: systemWhite
    },
    semantic: {
      success: semanticSuccess,
      warning: semanticWarning,
      danger: semanticDanger,
      info: semanticInfo
    },
    fill: {
      'scrollbar-tabbar': fillScrollbarTabbar,
      hover: fillHover
    }
  }
} = useTheme();
const colorMap = new Map([['yesno', semanticInfo], ['success', semanticSuccess], ['warning', semanticWarning], ['danger', semanticDanger], ['info', semanticInfo]]);
const titleMap = new Map([['yesno', 'notify'], ['success', 'confirm'], ['warning', 'warning'], ['danger', 'error'], ['info', 'notify']]);
const iconMap = new Map([['yesno', 'Info'], ['success', 'ApprovalFilled'], ['warning', 'Warning'], ['danger', 'Failed'], ['info', 'Info']]);
const fadeInDown = animations.fadeInDown;
const fadeOutUp = animations.fadeOutUp;
const Popup = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  cancelProps,
  className,
  confirmProps,
  description,
  icon,
  noText,
  onCancel,
  onClose,
  onConfirm,
  open,
  pressESCToClose,
  style,
  subtitle,
  title,
  top,
  type,
  width,
  yesText,
  ...props
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const statusAction = useRef('');
  const [openState, setOpenState] = useState(open);
  const [custom, setCustom] = useState(null);
  const [showMoreDescription, setShowMoreDescription] = useState(false);
  const [descriptionLines, setDescriptionLines] = useState(0);
  const showPopup = useDelayUnmount(openState, 200);
  const _PopupRootCSS = PopupRootCSS(width, top, type, description, subtitle, showMoreDescription, descriptionLines);
  const pressESCHandler = event => {
    if (event.key === 'Escape') {
      onClosePopup('isClose');
    }
  };
  const onShowPopup = (options, cb) => {
    if (options && typeof options === 'object' && Object.keys(options).length) {
      const newProps = {
        type: options.type !== undefined ? options.type : type,
        title: options.title !== undefined ? options.title : title,
        icon: options.icon !== undefined ? options.icon : icon,
        description: options.description !== undefined ? options.description : description,
        yesText: options.yesText !== undefined ? options.yesText : yesText,
        noText: options.noText !== undefined ? options.noText : noText,
        confirmProps: options.confirmProps !== undefined ? options.confirmProps : confirmProps,
        cancelProps: options.cancelProps !== undefined ? options.cancelProps : cancelProps,
        onCancel: cb ? () => cb(false) : options.onCancel !== undefined ? options.onCancel : onCancel,
        onConfirm: cb ? () => cb(true) : options.onConfirm !== undefined ? options.onConfirm : onConfirm
      };
      const popup = jsx(Fragment, null, PopupHeaderView(newProps.title, newProps.type), jsx("div", {
        className: 'DGN-UI-Popup-Body'
      }, newProps.icon !== false ? jsx("div", {
        className: 'DGN-UI-Popup-Body-Icon'
      }, jsx(Icon, {
        name: newProps.icon === true ? iconMap.get(newProps.type) : newProps.icon,
        width: 40,
        height: 40,
        color: colorMap.get(newProps.type)
      })) : null, jsx("div", {
        className: 'DGN-UI-Popup-Body-Description'
      }, jsx(Typography, {
        type: 'p2'
      }, newProps.description))), PopupActionView(newProps.onConfirm, newProps.confirmProps, newProps.onCancel, newProps.cancelProps, newProps.type, newProps.yesText, newProps.noText));
      setCustom(popup);
    } else {
      setCustom(false);
    }
    setOpenState(true);
  };
  const onClosePopup = mode => {
    if (onClose) {
      setOpenState(false);
      statusAction.current = mode;
    } else {
      if (ref.current) {
        setOpenState(false);
      }
    }
  };
  const onRefChange = useCallback(node => {
    if (node) {
      setDescriptionLines(Math.round((node === null || node === void 0 ? void 0 : node.offsetHeight) / 18)); //18 is line-height of Typography p2
      setShowMoreDescription(false);
    }
  }, []);
  useEffect(() => {
    if (open) {
      setOpenState(open);
      setShowMoreDescription(true);
    } else if (openState) {
      onClosePopup('isClose');
    }
  }, [open]);
  useEffect(() => {
    if (openState) {
      if (pressESCToClose) {
        document.addEventListener('keydown', pressESCHandler);
      }
    } else {
      setCustom(null);
      if (onClose) onClose({
        action: statusAction.current
      });
    }
    return () => {
      document.removeEventListener('keydown', pressESCHandler);
    };
  }, [openState]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {
      show: onShowPopup,
      close: onClosePopup,
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.show = onShowPopup;
    currentRef.close = onClosePopup;
    return currentRef;
  });
  const PopupHeaderView = (title, type) => {
    return jsx("div", {
      className: 'DGN-UI-Popup-Header'
    }, jsx(Typography, {
      className: 'DGN-UI-Popup-Header-Title',
      type: 'h3',
      color: 'inherit'
    }, title !== null && title !== void 0 ? title : getGlobal(titleMap.get(type))), jsx(ButtonIcon, {
      circular: true,
      viewType: 'text',
      color: type === 'yesno' ? 'info' : type,
      name: 'Close',
      onClick: () => onClosePopup('isClose')
    }));
  };
  const PopupBodyView = (icon, type, subtitle, description) => {
    return jsx("div", {
      className: 'DGN-UI-Popup-Body'
    }, icon !== false ? jsx("div", {
      className: 'DGN-UI-Popup-Body-Icon'
    }, jsx(Icon, {
      name: icon === true ? iconMap.get(type) : icon,
      width: 40,
      height: 40,
      color: colorMap.get(type)
    })) : null, jsx("div", {
      className: 'DGN-UI-Popup-Body-Description'
    }, jsx(Typography, {
      type: 'p2'
    }, subtitle ? jsx(Typography, {
      className: 'DGN-UI-Popup-Body-Subtitle',
      fullWidth: true
    }, subtitle) : null, jsx(Typography, {
      ref: onRefChange,
      type: 'p2',
      className: 'DGN-UI-Popup-Body-Detail'
    }, description), subtitle && descriptionLines > 1 || descriptionLines > 3 ? jsx("span", {
      className: 'DGN-More-Action',
      onClick: () => setShowMoreDescription(!showMoreDescription)
    }, getGlobal(showMoreDescription ? 'showLess' : 'showMore')) : null)));
  };
  const PopupActionView = (onConfirm, confirmProps, onCancel, cancelProps, type, yesText, noText) => {
    return onConfirm || onCancel || type === 'yesno' ? jsx("div", {
      className: 'DGN-UI-Popup-Action'
    }, onConfirm || type === 'yesno' ? jsx(Button, {
      viewType: 'filled',
      label: yesText || getGlobal('ok').toUpperCase(),
      color: type === 'yesno' ? 'info' : type,
      onClick: () => {
        onClosePopup('isConfirm');
        onConfirm && onConfirm();
      },
      ...confirmProps
    }) : null, onCancel || type === 'yesno' ? jsx(Button, {
      viewType: 'outlined',
      label: noText || getGlobal('cancel').toUpperCase(),
      color: type === 'yesno' ? 'info' : type,
      onClick: () => {
        onClosePopup('isCancel');
        onCancel && onCancel();
      },
      ...cancelProps
    }) : null) : null;
  };
  return showPopup && /*#__PURE__*/createPortal(jsx("div", {
    css: _PopupRootCSS,
    ref: ref,
    style: style,
    className: classNames('DGN-UI-Portal DGN-UI-Popup-Portal', className)
  }, jsx("div", {
    className: classNames('DGN-UI-Popup', openState ? 'DGN-Show' : 'DGN-Hide'),
    ...props
  }, custom ? custom : jsx(Fragment, null, PopupHeaderView(title, type), PopupBodyView(icon, type, subtitle, description), PopupActionView(onConfirm, confirmProps, onCancel, cancelProps, type, yesText, noText)))), document.body);
}));
const PopupRootCSS = (width, top, type, description, subtitle, showMoreDescription, descriptionLines) => css`
    ${displayFlex};
    ${flexRow};
    ${positionFixed};
    ${justifyCenter};
    ${breakWord};
    ${parseWidthHeight('100%')};
    ${inset(0)};
    ${bgColor('rgba(21, 26, 48, 0.5)')}
    ${z(2)};
    .DGN-UI-Popup {
        ${displayFlex};
        ${flexCol};
        ${boxBorder};
        ${borderRadius(4)};
        ${parseWidthHeight(width, 'fit-content')};
        ${parseMaxWidthHeight('80%')};
        ${bgColor(systemWhite)};
        margin: ${isNaN(top) ? top : top + 'px'} auto auto;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
        &.DGN-Show {
            animation: ${fadeInDown} 0.2s ease;
        }
        &.DGN-Hide {
            animation: ${fadeOutUp} 0.3s ease;
            animation-fill-mode: forwards;
        }
        .DGN-UI-Popup-Header {
            ${displayFlex};
            ${flexRow};
            ${itemsCenter};
            ${parseMinHeight(40)};
            ${pd([0, 2, 0, 4])};
            ${borderRadius('4px 4px 0px 0px')};
            .DGN-UI-Popup-Header-Title {
                ${displayFlex};
                ${flexRow};
                ${truncate};
                ${parseWidth('calc(100% - 24px)')};
            }
            color: ${colorMap.get(type)};
            ${bgColor(hexToRGBA(colorMap.get(type), 0.15))};
        }
        .DGN-UI-Popup-Body {
            ${displayFlex};
            ${flexRow};
            ${positionRelative};
            ${overflowAuto};
            ${pd([4])};
            flex: 1 1 auto;
            &::-webkit-scrollbar {
                ${borderRadius(4)};
                ${parseWidth(24)};
                background-color: ${systemWhite} !important;
            }
            &::-webkit-scrollbar-thumb {
                ${border(8, 'transparent')};
                ${borderRadius(24)};
                mix-blend-mode: normal;
                background-color: ${fillScrollbarTabbar} !important;
                background-clip: content-box;
            }
            &::-webkit-scrollbar-thumb:hover {
                ${border(8, 'transparent')};
                ${borderRadius(24)};
                mix-blend-mode: normal;
                background-color: ${fillHover} !important;
                background-clip: content-box;
            }
            .DGN-UI-Popup-Body-Icon {
                ${displayBlock};
                ${borderRadius50};
                ${parseWidth(40)};
                ${mgr([4])};
            }
            .DGN-UI-Popup-Body-Description {
                ${displayFlex};
                ${flexRow};
                ${positionRelative};
                ${itemsCenter};
                ${parseMinHeight(40)};
                ${parseHeight('max-content')};
                .DGN-UI-Popup-Body-Subtitle {
                    ${mgb([(description === null || description === void 0 ? void 0 : description.length) > 0 ? 2 : 0])};
                }
                .DGN-UI-Popup-Body-Detail {
                    ${overflowHidden};
                    ${textEllipsis};
                    display: -webkit-box;
                    -webkit-line-clamp: ${showMoreDescription ? 'none' : subtitle && descriptionLines > 1 ? 1 : descriptionLines > 3 ? 3 : 'none'};
                    -webkit-box-orient: vertical;
                }
                .DGN-More-Action {
                    ${displayBlock};
                    ${cursorPointer};
                    ${parseWidth('fit-content')};
                    ${mgt([4])};
                    ${mgb([type === 'yesno' ? 0 : 2])};
                    ${textUnderline};
                    ${textColor('semantic.info')};
                }
            }
        }
        .DGN-UI-Popup-Action {
            ${displayFlex};
            ${flexRow};
            ${justifyEnd};
            ${pd([2, 4, 4, 4])};
            .DGN-UI-Button {
                ${parseMinWidth(80)};
                ${mg(0)};
                &.filled {
                    ${mgr([2])};
                }
            }
        }
    }
`;
Popup.defaultProps = {
  className: '',
  description: '',
  icon: true,
  open: false,
  pressESCToClose: false,
  style: {},
  top: 56,
  type: 'info',
  width: 480
};
Popup.propTypes = {
  /** If `true`, the component is shown. */
  open: PropTypes.bool,
  /** [Props](https://core.diginet.com.vn/ui/?path=/story/button) applied to the cancel `Button`. */
  cancelProps: PropTypes.object,
  /** Class for component. */
  className: PropTypes.string,
  /** [Props](https://core.diginet.com.vn/ui/?path=/story/button) applied to the confirm `Button`. */
  confirmProps: PropTypes.object,
  /** Details content of the component. */
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic) display before content. */
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Label of cancel button. */
  noText: PropTypes.string,
  /** Callback fired when click on cancel button. */
  onCancel: PropTypes.func,
  /** Callback fired when click on confirm button. */
  onConfirm: PropTypes.func,
  /** If `true`, hitting escape will close component. */
  pressESCToClose: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** Main content of the component. */
  subtitle: PropTypes.node,
  /** Title of the component. */
  title: PropTypes.node,
  /** Top position of the component. */
  top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Type of the component. */
  type: PropTypes.oneOf(['info', 'success', 'warning', 'danger', 'yesno']),
  /** Width of the component. */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Label of confirm button. */
  yesText: PropTypes.string
  /**
   * ref methods
   *
   * how to get component element? ref.current
   *
   * how to call method? ref.current.instance.{method}
   *
   * * show(options, callback): Show popup
   *  * @param {options} - object
   *      * {type, title, icon, yesNo, description, confirmProps, cancelProps, onCancel, onConfirm}
   *  * @param {callback} - func
   *      * Callback fired after click on button (callback(true) with confirm Button and callback(false) with cancel Button). Alternate both onCancel, onConfirm methods of options
   * * close(): Close popup
   */
};

export default Popup;